import { Badge, Button, Checkbox, Dialog, Notice } from '@mogzo/ui';
import { TriangleAlert, Upload } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { ApiError, apiSend } from '../lib/api';
import {
  type CopyItem,
  type CopyPreview,
  countOf,
  ruleDoes,
  ruleWhen,
  type Schedule,
  type ScheduleEvent,
  type ScheduleRule,
  shown,
  whenOf,
} from '../lib/schedule';
import { useApi } from '../lib/use-api';
import { Loading } from './ui';

/** What the dialog starts with ticked: these, or (null) everything new or changed. */
export interface CopyPicks {
  events: string[];
  rules: number[];
}

export interface CopyResult {
  events: number;
  rules: number;
  removed: number;
  missing: string[];
}

const TAGS = {
  new: { tone: 'info', label: 'New' },
  changed: { tone: 'warning', label: 'Changed' },
  same: { tone: 'neutral', label: 'Same as live' },
} as const;

const lowerFirst = (text: string) => text.charAt(0).toLowerCase() + text.slice(1);
const whenText = (event: ScheduleEvent) => {
  const when = whenOf(event);
  return when.sub ? `${when.main}, ${when.sub}` : when.main;
};

/** "When Weekly best starts fresh, give the top 10 “Weekly champion”": a rule in one line. */
export function ruleLine(
  rule: ScheduleRule,
  schedule: Pick<Schedule, 'boards' | 'events'> & Partial<Pick<Schedule, 'achievements'>>,
) {
  const when = ruleWhen(rule, schedule).main;
  const does = ruleDoes(rule, schedule.achievements ?? []);
  const doing = [lowerFirst(does.lead), does.key, does.tail].filter(Boolean).join(' ');
  return rule.trigger === 'times' ? `${when}, ${doing}` : `When ${when}, ${doing}`;
}

/** What differs between test's event and live's, in words. */
function eventChanges(test: ScheduleEvent, live: ScheduleEvent): string[] {
  const changes: string[] = [];
  if (test.name !== live.name) changes.push(`Called “${test.name}” instead of “${live.name}”`);
  if (whenText(test) !== whenText(live)) {
    changes.push(`${whenText(test)} instead of ${whenText(live)}`);
  }
  const keys = new Set([...Object.keys(test.config), ...Object.keys(live.config)]);
  for (const key of keys) {
    const now = test.config[key];
    const before = live.config[key];
    if (JSON.stringify(now) === JSON.stringify(before)) continue;
    if (!(key in test.config)) changes.push(`No longer sets ${key}`);
    else if (!(key in live.config)) changes.push(`Sets ${key} to ${shown(now)}`);
    else changes.push(`${key} ${shown(now)} instead of ${shown(before)}`);
  }
  if (test.board?.from !== live.board?.from) {
    changes.push(
      !test.board
        ? 'No leaderboard of its own'
        : !live.board
          ? 'Gets a leaderboard of its own'
          : `Its board counts ${test.board.from} instead of ${live.board.from}`,
    );
  } else if (test.board && live.board && test.board.name !== live.board.name) {
    changes.push(`Its board is called “${test.board.name}”`);
  }
  if (test.enabled !== live.enabled) changes.push(test.enabled ? 'Turned on' : 'Turned off');
  return changes;
}

function ruleChanges(
  test: ScheduleRule,
  live: ScheduleRule,
  schedule: Pick<Schedule, 'boards' | 'events'>,
): string[] {
  const changes: string[] = [];
  const line = (rule: ScheduleRule) => ruleLine(rule, schedule);
  const sub = (rule: ScheduleRule) => ruleDoes(rule, []).sub;
  if (line(test) !== line(live)) changes.push(`Instead of: ${lowerFirst(line(live))}`);
  else if (sub(test) !== sub(live))
    changes.push(`${sub(test)} instead of ${lowerFirst(sub(live))}`);
  if (test.enabled !== live.enabled) changes.push(test.enabled ? 'Turned on' : 'Turned off');
  return changes;
}

function Row({
  id,
  checked,
  disabled,
  onChange,
  title,
  detail,
  note,
  warn,
  tag,
}: {
  id: string;
  checked: boolean;
  disabled?: boolean;
  onChange: () => void;
  title: ReactNode;
  detail: ReactNode;
  note?: ReactNode;
  warn?: ReactNode;
  tag: ReactNode;
}) {
  return (
    <div
      className={`flex items-start gap-3 border-t border-line py-3 ${disabled ? 'opacity-60' : ''}`}
    >
      <Checkbox
        id={id}
        className="min-w-0 flex-1"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        label={title}
        hint={
          <span className="flex flex-col gap-0.5">
            <span>{detail}</span>
            {note && <span className="text-ink">{note}</span>}
            {warn && (
              <span className="flex items-start gap-1.5 text-ink">
                <TriangleAlert
                  size={14}
                  aria-hidden="true"
                  className="mt-[3px] shrink-0 text-warning"
                />
                <span>{warn}</span>
              </span>
            )}
          </span>
        }
      />
      {tag}
    </div>
  );
}

const sectionTitle = 'm-0 mb-2 text-[12px] leading-4 font-semibold tracking-[0.06em] text-muted';

/**
 * Copy to live (Nov 3, 2026, the approved mockup): test's events and rules, each new, changed
 * or the same as live's, ticked one by one. Live gets only what's ticked; anything left
 * unticked stays on live as it is. Live-only ones go to live's Trash only when ticked.
 */
export function CopyToLive({
  projectId,
  schedule,
  picks,
  onClose,
  onCopied,
}: {
  projectId: string;
  /** The test schedule, for names of boards and events. */
  schedule: Schedule;
  picks: CopyPicks | null;
  onClose: () => void;
  onCopied: (result: CopyResult) => void;
}) {
  const preview = useApi<CopyPreview>(`/v1/dev/projects/${projectId}/schedule/copy-to-live`);
  if (preview.status !== 'ready') {
    return (
      <Dialog open title="Copy to live" onClose={onClose}>
        {preview.status === 'loading' ? (
          <Loading />
        ) : (
          <Notice tone="danger">{preview.error.message}</Notice>
        )}
      </Dialog>
    );
  }
  return (
    <Picker
      projectId={projectId}
      schedule={schedule}
      preview={preview.data}
      picks={picks}
      onClose={onClose}
      onCopied={onCopied}
    />
  );
}

function Picker({
  projectId,
  schedule,
  preview,
  picks,
  onClose,
  onCopied,
}: {
  projectId: string;
  schedule: Schedule;
  preview: CopyPreview;
  picks: CopyPicks | null;
  onClose: () => void;
  onCopied: (result: CopyResult) => void;
}) {
  const changes = {
    events: preview.events.filter((item) => item.state !== 'same').map((item) => item.test.id),
    rules: preview.rules.filter((item) => item.state !== 'same').map((item) => item.test.id),
  };
  // A rule that runs when an event ends needs that event on live: ticking it ticks the event.
  const needs = (rule: ScheduleRule) =>
    rule.trigger === 'event_end' &&
    preview.events.some((item) => item.test.id === rule.event && !item.live)
      ? rule.event
      : null;
  const withNeeds = (next: CopyPicks): CopyPicks => {
    const events = new Set(next.events);
    for (const item of preview.rules) {
      const event = needs(item.test);
      if (event && next.rules.includes(item.test.id)) events.add(event);
    }
    return { events: [...events], rules: next.rules };
  };
  const [ticked, setTicked] = useState<CopyPicks>(() =>
    withNeeds(
      picks
        ? {
            events: picks.events.filter((id) => changes.events.includes(id)),
            rules: picks.rules.filter((id) => changes.rules.includes(id)),
          }
        : changes,
    ),
  );
  const [remove, setRemove] = useState<CopyPicks>({ events: [], rules: [] });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleEvent = (id: string) =>
    setTicked((now) =>
      now.events.includes(id)
        ? {
            events: now.events.filter((e) => e !== id),
            // Unticking an event unticks the rules that need it.
            rules: now.rules.filter(
              (r) =>
                needs(preview.rules.find((item) => item.test.id === r)?.test as ScheduleRule) !==
                id,
            ),
          }
        : { ...now, events: [...now.events, id] },
    );
  const toggleRule = (id: number) =>
    setTicked((now) =>
      withNeeds(
        now.rules.includes(id)
          ? { ...now, rules: now.rules.filter((r) => r !== id) }
          : { ...now, rules: [...now.rules, id] },
      ),
    );
  const toggleRemove = (kind: 'events' | 'rules', id: string | number) =>
    setRemove((now) => {
      const list = now[kind] as (string | number)[];
      const next = list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
      return { ...now, [kind]: next };
    });

  const copying = ticked.events.length + ticked.rules.length;
  const removing = remove.events.length + remove.rules.length;
  const total = changes.events.length + changes.rules.length;
  const label = copying
    ? `Copy ${copying} to live${removing ? `, remove ${removing}` : ''}`
    : removing
      ? `Remove ${removing} from live`
      : 'Copy to live';
  const both = {
    boards: schedule.boards,
    events: schedule.events,
    achievements: schedule.achievements,
  };

  async function copy() {
    setBusy(true);
    setError(null);
    try {
      const result = await apiSend<CopyResult>(
        'POST',
        `/v1/dev/projects/${projectId}/schedule/copy-to-live`,
        { ...ticked, remove },
      );
      onCopied(result);
    } catch (failed) {
      setError(failed instanceof ApiError ? failed.message : 'Could not reach the API. Try again.');
      setBusy(false);
    }
  }

  const missingWarn = (item: CopyItem<unknown>) =>
    item.missing.length
      ? `Live has no ${item.missing.join(' or ')} yet. It fails until a version that has it is live.`
      : undefined;

  return (
    <Dialog
      open
      title="Copy to live"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            icon={Upload}
            onClick={copy}
            loading={busy}
            disabled={!copying && !removing}
          >
            {label}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <p className="m-0 text-muted">
          Live gets what you tick. Anything you leave unticked stays on live as it is.
        </p>
        {error && <Notice tone="danger">{error}</Notice>}
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={() => setTicked(withNeeds(changes))}>
            Only changes
          </Button>
          <Button
            size="sm"
            onClick={() => {
              setTicked({ events: [], rules: [] });
              setRemove({ events: [], rules: [] });
            }}
          >
            Untick all
          </Button>
          <span className="ml-auto text-[13px] text-muted">
            {copying} of {total} {total === 1 ? 'change' : 'changes'} ticked
          </span>
        </div>

        {preview.events.length > 0 && (
          <section aria-label="Events">
            <h3 className={sectionTitle}>EVENTS</h3>
            {preview.events.map((item) => {
              const event = item.test;
              const tag = TAGS[item.state];
              const detail =
                item.state === 'same'
                  ? 'Nothing to copy'
                  : item.live
                    ? eventChanges(event, item.live).join(' · ')
                    : [
                        'Not on live yet',
                        whenText(event),
                        event.board ? 'Own leaderboard' : null,
                        event.enabled ? null : 'Turned off on test, so it’s copied turned off',
                      ]
                        .filter(Boolean)
                        .join(' · ');
              return (
                <Row
                  key={event.id}
                  id={`copy-event-${event.id}`}
                  checked={ticked.events.includes(event.id)}
                  disabled={item.state === 'same'}
                  onChange={() => toggleEvent(event.id)}
                  title={
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{event.name}</span>
                      <span className="font-mono text-[12px] text-subtle">{event.id}</span>
                    </span>
                  }
                  detail={detail}
                  warn={missingWarn(item)}
                  tag={<Badge tone={tag.tone}>{tag.label}</Badge>}
                />
              );
            })}
          </section>
        )}

        {preview.rules.length > 0 && (
          <section aria-label="Rules">
            <h3 className={sectionTitle}>RULES</h3>
            {preview.rules.map((item) => {
              const rule = item.test;
              const tag = TAGS[item.state];
              const event = needs(rule);
              const eventName = schedule.events.find((e) => e.id === event)?.name ?? event;
              return (
                <Row
                  key={rule.id}
                  id={`copy-rule-${rule.id}`}
                  checked={ticked.rules.includes(rule.id)}
                  disabled={item.state === 'same'}
                  onChange={() => toggleRule(rule.id)}
                  title={<span className="font-medium">{ruleLine(rule, both)}</span>}
                  detail={
                    item.state === 'same'
                      ? 'Nothing to copy'
                      : item.live
                        ? ruleChanges(rule, item.live, both).join(' · ')
                        : 'Not on live yet'
                  }
                  note={
                    event && item.state !== 'same'
                      ? `Needs ${eventName} on live, so ticking it ticks ${eventName} too.`
                      : undefined
                  }
                  warn={missingWarn(item)}
                  tag={<Badge tone={tag.tone}>{tag.label}</Badge>}
                />
              );
            })}
          </section>
        )}

        {(preview.onlyLive.events.length > 0 || preview.onlyLive.rules.length > 0) && (
          <section aria-label="Only on live">
            <h3 className={`${sectionTitle} mb-1`}>ONLY ON LIVE</h3>
            <p className="m-0 mb-2 text-[13px] text-muted">
              These aren’t on your test schedule. They stay on live unless you tick them.
            </p>
            {preview.onlyLive.events.map((event) => {
              const checked = remove.events.includes(event.id);
              return (
                <Row
                  key={event.id}
                  id={`remove-event-${event.id}`}
                  checked={checked}
                  onChange={() => toggleRemove('events', event.id)}
                  title={
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">Remove {event.name} from live</span>
                      <span className="font-mono text-[12px] text-subtle">{event.id}</span>
                    </span>
                  }
                  detail={[whenText(event), event.board ? 'Own leaderboard' : null]
                    .filter(Boolean)
                    .join(' · ')}
                  note={
                    checked && (
                      <span className="text-danger">
                        It goes to Trash and stops running.
                        {event.board && ' Its leaderboard’s scores are kept there for 30 days.'}
                      </span>
                    )
                  }
                  tag={<Badge tone="outline">Only on live</Badge>}
                />
              );
            })}
            {preview.onlyLive.rules.map((rule) => {
              const checked = remove.rules.includes(rule.id);
              return (
                <Row
                  key={rule.id}
                  id={`remove-rule-${rule.id}`}
                  checked={checked}
                  onChange={() => toggleRemove('rules', rule.id)}
                  title={
                    <span className="font-medium">
                      Remove from live: {lowerFirst(ruleLine(rule, both))}
                    </span>
                  }
                  detail={ruleDoes(rule, []).sub}
                  note={
                    checked && (
                      <span className="text-danger">It goes to Trash and stops running.</span>
                    )
                  }
                  tag={<Badge tone="outline">Only on live</Badge>}
                />
              );
            })}
          </section>
        )}
      </div>
    </Dialog>
  );
}

/** What a copy did, for the Schedule tab's message. */
export function copiedText(result: CopyResult): { tone: 'success' | 'warning'; text: string } {
  const parts = [];
  if (result.events + result.rules) {
    parts.push(`Copied ${countOf(result.events, result.rules)} to live.`);
  }
  if (result.removed) parts.push(`Moved ${result.removed} from live to Trash.`);
  if (result.missing.length) {
    parts.push(
      `Live doesn’t have ${result.missing.join(', ')} yet, so what needs them fails until a published version brings them.`,
    );
  }
  return { tone: result.missing.length ? 'warning' : 'success', text: parts.join(' ') };
}
